import { useId, useState } from "react"
import type { AxiosError } from "axios"
import { Eye, EyeOff } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import type { ProblemDetail } from "@/lib/api-client"
import { authApi } from "@/features/auth/api/auth.api"
import { useAuthStore } from "@/store/auth.store"

const inputClass =
  "mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"

/**
 * The account changes its own password (current password required). Used in
 * Settings, from the account menu, and as the forced first change after an
 * administrator set the password. The API ends every other session.
 */
export function ChangePasswordForm({ onDone, submitLabel = "Change password" }: { onDone?: () => void; submitLabel?: string }) {
  const id = useId()
  const [currentPw, setCurrentPw] = useState("")
  const [newPw, setNewPw] = useState("")
  const [confirmPw, setConfirmPw] = useState("")
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null)

  const checks = [
    { ok: newPw.length >= 12, label: "At least 12 characters" },
    { ok: /[a-z]/.test(newPw) && /[A-Z]/.test(newPw), label: "Upper- and lower-case letters" },
    { ok: /\d/.test(newPw), label: "A digit" },
    { ok: /[^A-Za-z0-9]/.test(newPw), label: "A symbol" },
    { ok: newPw !== "" && newPw !== currentPw, label: "Different from the current password" },
    { ok: newPw !== "" && newPw === confirmPw, label: "Passwords match" },
  ]
  const valid = currentPw !== "" && checks.every((c) => c.ok)

  const submit = async () => {
    if (!valid || busy) return
    setBusy(true)
    setStatus(null)
    try {
      await authApi.changePassword({ current_password: currentPw, new_password: newPw })
      // The API cleared any "must change" flag: refresh the account
      useAuthStore.getState().setMe(await authApi.getMe())
      setCurrentPw(""); setNewPw(""); setConfirmPw("")
      setStatus({ ok: true, text: "Password changed. Your other sessions have been signed out." })
      onDone?.()
    } catch (err) {
      const e = err as AxiosError<ProblemDetail>
      const detail = e.response?.data?.errors?.[0]?.message ?? e.response?.data?.detail
      setStatus({ ok: false, text: detail ?? "The password could not be changed." })
    } finally {
      setBusy(false)
    }
  }

  const field = (key: string, label: string, value: string, onChange: (v: string) => void, autoComplete: string) => (
    <div>
      <label htmlFor={`${id}-${key}`} className="block text-xs font-medium text-muted-foreground">{label}</label>
      <input id={`${id}-${key}`} type={show ? "text" : "password"} value={value} autoComplete={autoComplete} maxLength={128}
        onChange={(e) => onChange(e.target.value)} className={inputClass} />
    </div>
  )

  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void submit() }}>
      {field("current", "Current password", currentPw, setCurrentPw, "current-password")}
      {field("new", "New password", newPw, setNewPw, "new-password")}
      {field("confirm", "Confirm new password", confirmPw, setConfirmPw, "new-password")}
      <button type="button" onClick={() => setShow((v) => !v)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        {show ? "Hide passwords" : "Show passwords"}
      </button>
      {newPw && (
        <ul className="space-y-1" aria-label="Password requirements">
          {checks.map((c) => (
            <li key={c.label} className="flex items-center gap-1.5 text-xs">
              <span className={cn("h-1.5 w-1.5 rounded-full", c.ok ? "bg-emerald-500" : "bg-red-500")} aria-hidden />
              <span className={c.ok ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}>
                {c.label}{c.ok ? "" : " (not yet)"}
              </span>
            </li>
          ))}
        </ul>
      )}
      {status && (
        <p role={status.ok ? "status" : "alert"} className={cn("text-xs", status.ok ? "text-emerald-700 dark:text-emerald-400" : "text-destructive")}>
          {status.text}
        </p>
      )}
      <div className="flex justify-end pt-1">
        <Button type="submit" disabled={!valid || busy}>{busy ? "Saving…" : submitLabel}</Button>
      </div>
    </form>
  )
}
